import { apiConfig } from '@/config/api.config'
import { AUTH } from '@/constants/auth'
import { useAuthStore } from '@/stores/auth.store'
import axios, { AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import Cookies from 'js-cookie'

export const axiosInstance = axios.create({
  baseURL: apiConfig.VITE_PUBLIC_API_URL,
})

export const httpClient = () => {
  axiosInstance.defaults.headers.common['teamId'] =
    useAuthStore.getState().activeTeam?.id
  return axiosInstance
}

axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: (retryCount, error) =>
    axiosRetry.exponentialDelay(retryCount, error, 300),
  retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
})

function refreshTokenAPI(token: string) {
  return axios.get(`${apiConfig.VITE_PUBLIC_API_URL}/auth/refresh-tokens`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

axiosInstance.interceptors.request.use((request) => {
  const headers = request.headers ?? {}
  const token = Cookies.get(AUTH.ACCESS_TOKEN)

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  request.headers = headers
  return request
})

type FailedRequestQueue = {
  onSuccess: (newToken: string) => void
  onFailure: () => void
}

let failedRequestsQueue: FailedRequestQueue[] = []
let isRefreshing = false

axiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const refreshToken = Cookies.get(AUTH.REFRESH_TOKEN)
      //Uma sugestão aqui é verificar o tipo do erro retornando
      //pois um token pode ser inválido, por exemplo, e nesse caso
      //gerar um novo token usando o refresh_token pode ser um
      //problema de segurança. Logo o token tem que ser válido
      //porém está expirado.
      //E para fechar com chave de ouro esse ponto,
      //ao fazer o refresh token o backend deverá invalidar o token.

      //Somado a isso verificar se há um refresh token salvo
      // TODO: error.response.data?.code === "token.expired" && refreshToken
      if (refreshToken) {
        //Busca as informações da request. Com elas é possível
        //repetir a request passando as mesmas informações
        const originalRequest = error.config

        if (!isRefreshing) {
          isRefreshing = true

          refreshTokenAPI(refreshToken)
            .then(({ data }) => {
              Cookies.set(AUTH.ACCESS_TOKEN, data.access_token)
              Cookies.set(AUTH.REFRESH_TOKEN, data.refresh_token)

              for (const request of failedRequestsQueue) {
                request.onSuccess(data.access_token)
              }
            })
            .catch((_) => {
              for (const request of failedRequestsQueue) {
                request.onFailure()
              }
            })
            .finally(() => {
              failedRequestsQueue = []
              isRefreshing = false
            })
        }

        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            onSuccess: (newToken: string) => {
              if (originalRequest) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                resolve(axiosInstance(originalRequest))
              }
            },
            onFailure: () => {
              reject(error)
            },
          })
        })
      }

      Cookies.remove(AUTH.ACCESS_TOKEN)
      Cookies.remove(AUTH.REFRESH_TOKEN)
      if (
        !window.location.href.endsWith('/sign-in') &&
        !window.location.href.endsWith('/forgot-password') &&
        !window.location.href.includes('/reset-password')
      ) {
        console.log(window.location.href)
        window.location.href = '/sign-in'
      }
    }
    return Promise.reject(error)
  }
)
